import dayjs from "dayjs";
import { Tag } from "antd";

export const PLAN_TYPE_OPTIONS = [
  { value: "PERSONAL", label: "Personal" },
  { value: "BUSINESS", label: "Business" },
];

export const SUBSCRIBER_TYPE_OPTIONS = [
  { value: "USER", label: "User" },
  { value: "BUSINESS", label: "Business" },
];

export const PAYMENT_TYPE_OPTIONS = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "ANNUALLY", label: "Annually" },
];

export const PAYMENT_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
  { value: "FAILED", label: "Failed" },
  { value: "REFUNDED", label: "Refunded" },
];

export const CURRENCY_OPTIONS = [
  { value: "AED", label: "AED" },
  { value: "AUD", label: "AUD" },
  { value: "BRL", label: "BRL" },
  { value: "CAD", label: "CAD" },
  { value: "CDF", label: "CDF" },
  { value: "CHF", label: "CHF" },
  { value: "CNY", label: "CNY" },
  { value: "DKK", label: "DKK" },
  { value: "ETB", label: "ETB" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "GHS", label: "GHS" },
  { value: "HKD", label: "HKD" },
  { value: "ILS", label: "ILS" },
  { value: "INR", label: "INR" },
  { value: "JPY", label: "JPY" },
  { value: "KES", label: "KES" },
  { value: "KRW", label: "KRW" },
  { value: "MWK", label: "MWK" },
  { value: "MXN", label: "MXN" },
  { value: "MYR", label: "MYR" },
  { value: "NGN", label: "NGN" },
  { value: "NOK", label: "NOK" },
  { value: "NZD", label: "NZD" },
  { value: "PHP", label: "PHP" },
  { value: "RUB", label: "RUB" },
  { value: "RWF", label: "RWF" },
  { value: "SAR", label: "SAR" },
  { value: "SDG", label: "SDG" },
  { value: "SEK", label: "SEK" },
  { value: "SGD", label: "SGD" },
  { value: "THB", label: "THB" },
  { value: "TRY", label: "TRY" },
  { value: "UGX", label: "UGX" },
  { value: "USD", label: "USD" },
  { value: "XAF", label: "XAF" },
  { value: "XOF", label: "XOF" },
  { value: "ZAR", label: "ZAR" },
];

export const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "EXPIRED", label: "Expired" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "CANCELED", label: "Canceled" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "PENDING", label: "Pending" },
  { value: "TRIALING", label: "Trialing" },
  { value: "PAST_DUE", label: "Past due" },
  { value: "PAUSED", label: "Paused" },
];

export const PLAN_STATUS_OPTIONS = SUBSCRIPTION_STATUS_OPTIONS;

export const STATUS_TAG_COLORS = {
  ACTIVE: "green",
  EXPIRED: "volcano",
  CANCELLED: "red",
  CANCELED: "red",
  SUSPENDED: "orange",
  PENDING: "gold",
  TRIALING: "blue",
  PAST_DUE: "magenta",
  PAUSED: "default",
  PAID: "green",
  FAILED: "red",
  REFUNDED: "purple",
};

export const formatLabel = (value) =>
  value
    ? String(value)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (match) => match.toUpperCase())
    : "-";

export const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : String(value);
};

export const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatCurrency = (value, currency) => {
  if (value === null || value === undefined || value === "") return "-";
  if (!currency) return formatAmount(value);

  const number = Number(value);
  if (Number.isNaN(number)) return String(value);

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  } catch (error) {
    return `${formatAmount(number)} ${currency}`;
  }
};

export const pageFromResponse = (response) => {
  const content = Array.isArray(response?.content)
    ? response.content
    : Array.isArray(response)
    ? response
    : [];
  const total = response?.totalElements ?? content.length;
  return { content, total };
};

export const toDateValue = (value) => (value ? dayjs(value) : null);
export const toIsoValue = (value) => (value ? value.toISOString() : null);

export const getStatusTag = (status) => (
  <Tag color={STATUS_TAG_COLORS[String(status || "").toUpperCase()] || "default"}>
    {formatLabel(status)}
  </Tag>
);

export const getPlanFormValues = (plan = null) => ({
  name: plan?.name || "",
  code: plan?.code || "",
  description: plan?.description || "",
  subscriptionType: plan?.subscriptionType || "PERSONAL",
  status: plan?.status || "ACTIVE",
  startDate: toDateValue(plan?.startDate),
  expiryDate: toDateValue(plan?.expiryDate),
  monthlyAmount: plan?.monthlyAmount ?? null,
  annualAmount: plan?.annualAmount ?? null,
});

export const getSubscriptionFormValues = (subscription = null) => ({
  subscriberType: subscription?.subscriberType || "USER",
  subscriberId: subscription?.subscriberId || "",
  subscriptionPlanId: subscription?.subscriptionPlanId || undefined,
  currency: subscription?.currency || "USD",
  startDate: toDateValue(subscription?.startDate),
  endDate: toDateValue(subscription?.endDate),
  subscriptionPaymentType: subscription?.subscriptionPaymentType || "MONTHLY",
  paymentStatus: subscription?.paymentStatus || "PENDING",
  status: subscription?.status || "ACTIVE",
});

export const isFreePlanRecord = (record = null) => {
  if (!record) return false;

  const amount = Number(record?.amount ?? record?.monthlyAmount ?? record?.annualAmount);
  if (!Number.isNaN(amount) && amount <= 0) {
    return true;
  }

  const code = String(record?.subscriptionPlanCode || record?.code || "").toUpperCase();
  return code === "FREE_PERSONAL" || code === "FREE_BUSINESS";
};
